import { makeAutoObservable, runInAction } from 'mobx'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { toastStore } from 'shared/ui'
import { logger, replaceLink } from 'shared/lib'
import { CHAR_MAX_LENGTH } from 'shared/constants/limits'
import { uiStore } from 'shared/store/uiStore'
import { type IParamsCreateCannedMessages } from 'entities/CannedMessages'
import { ShortenLinkIconAction } from 'entities/ShortLink/ui/actions/ShortenLinkIconAction'
import { CannedMessage } from 'entities/CannedMessages/model/CannedMessage'
import { CannedMessagesApi } from 'entities/CannedMessages/api/savedReplies'
import { type MergeField } from 'entities/MergeField/model/MergeField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { MessageFieldStore } from 'widgets/MessageField/store/messageFieldStore'
import { MediaAction } from 'widgets/MessageField/ui/FieldActions/MediaAction/MediaAction'
import { EmojiAction } from 'widgets/MessageField/ui/FieldActions/EmojiAction'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'
import { mergeFieldReplacer } from 'widgets/MessageField/lib'
import { EnumVariantMessageField } from 'widgets/MessageField'
import { SavedRepliesPath } from 'pages/settings/pages/savedReplies/route/savedRepliesPath'
import { type INewSavedReplyStoreConfig } from './types'

export class NewSavedReplyStore {
  private _savedRepliesStore: INewSavedReplyStoreConfig['savedRepliesStore']
  private _messageFieldStore: MessageFieldStore
  private _title = ''
  private _isPublic = false
  private _is_favorite = false
  private _loading = false
  private _duplicate = false
  private _savedReplay: CannedMessage | null = null

  constructor(config: INewSavedReplyStoreConfig, private _entities: IEntitiesStore) {
    this._savedRepliesStore = config.savedRepliesStore
    this._messageFieldStore = new MessageFieldStore(
      {
        fromSavedReply: true,
        placeholder: 'Add a response (some details)',
        styles: {
          minHeight: 64,
        },
        showActionsItems: 4,
        variant: EnumVariantMessageField.Underline,
        makeActions: () => [
          {
            iconButtonComponent: <MediaAction />,
          },
          {
            iconButtonComponent: <EmojiAction />,
          },
          {
            iconButtonComponent: (
              <MergeFieldsIconAction
                onAddMergeField={(value) => this.onAddMergeField(value)}
                onClose={() => modalStore.closeAllModals()}
                isAllIntegration
                {...config.mergeFieldProps}
              />
            ),
          },
          {
            iconButtonComponent: (
              <ShortenLinkIconAction
                onAddShortLink={(link: string) =>
                  this._messageFieldStore.addContent &&
                  this._messageFieldStore.addContent(replaceLink(link) + '&nbsp;')
                }
              />
            ),
          },
        ],
      },
      this._entities
    )

    makeAutoObservable(this)
  }

  get loading() {
    return this._loading
  }

  get isPublic() {
    return this._isPublic
  }

  get createdByUser() {
    return this._entities.usersStore.getItem(this._savedReplay?.owner_id)
  }

  get messageFieldStore() {
    return this._messageFieldStore
  }

  get title() {
    return this._title
  }

  get isSharedReply() {
    return !!(
      this._savedRepliesStore.selectedSavedReply &&
      !this._savedRepliesStore.selectedSavedReply.isMyReply
    )
  }

  get hasChanges() {
    const currentSelectedSavedReply = this._savedRepliesStore.selectedSavedReply
    const mediaUrl = this._messageFieldStore.messageRequestData.media_url
    const message = this._messageFieldStore.messageRequestData.message
    const title = this._title

    if (this.isSharedReply && !this._savedRepliesStore.canManageAllSavedReplies) return false

    if (!currentSelectedSavedReply) {
      return !!mediaUrl.length || !!message.length || !!title.length
    }

    const hasAttachmentsChanges =
      mediaUrl.length !== currentSelectedSavedReply.attachments.length ||
      mediaUrl.some(
        (attachment) =>
          !currentSelectedSavedReply.attachments.some(
            (item) =>
              item.id === attachment.id ||
              item.id === attachment.url ||
              item.id === attachment.source
          )
      )

    return (
      hasAttachmentsChanges ||
      message !== currentSelectedSavedReply.message ||
      title !== currentSelectedSavedReply.title
    )
  }

  get isEdit() {
    return this._savedReplay && !this._duplicate
  }

  get showCreatedBy() {
    const user = this._entities.usersStore.user
    if (!user || !this._savedReplay) return false

    const isValidRole = user.isOwnerRole || user.isAdminRole

    return this.isEdit && isValidRole
  }

  get disabled() {
    return (
      this._title.length > CHAR_MAX_LENGTH ||
      this._loading ||
      this._messageFieldStore.loadingAttachment ||
      this._messageFieldStore.disablesSend ||
      !this._title.trim()
    )
  }

  get createSavedReplyRequest(): IParamsCreateCannedMessages {
    return {
      public: this._isPublic,
      is_favorite: this._is_favorite,
      media_url: this._messageFieldStore.messageRequestData.media_url,
      title: this._title,
      message: this._messageFieldStore.messageRequestData.message,
    }
  }

  reset = () => {
    this._isPublic = false
    this._title = ''
    this._messageFieldStore.reset()
  }

  onAddMergeField = (mergeField: MergeField) => {
    if (this._messageFieldStore.addContent) {
      this._messageFieldStore.addContent(
        mergeFieldReplacer(this._entities, mergeField.template) + '&nbsp;'
      )
    }
  }

  handleNewSavedReply = (savedReplay?: CannedMessage | null, duplicate?: boolean) => {
    this.reset()
    if (!savedReplay) this._savedRepliesStore.setSelectSavedReply(null)
    this._duplicate = duplicate || false
    this._savedReplay = savedReplay || null

    if (savedReplay) {
      this._title = savedReplay.title + (duplicate ? ' (Copy)' : '')
      this._isPublic = duplicate ? false : savedReplay.public
      this._is_favorite = duplicate ? false : savedReplay.is_favorite
      this._messageFieldStore.setMessageData({
        attachments: savedReplay.attachments,
        message: savedReplay.message,
        isReset: false,
        isFocus: false,
        isReplace: true,
      })
    } else {
      this._isPublic = false
      this._is_favorite = false
    }
  }

  setTitle = (title: string) => {
    this._title = title
  }

  setPublic = (isPublic: boolean) => {
    this._isPublic = isPublic
  }

  onCancel = () => {
    const currentSelectedSavedReply = this._savedRepliesStore.selectedSavedReply

    if (this.isEdit) {
      if (this.hasChanges) {
        this._savedRepliesStore.handleLeaveWithoutSaving(() => {
          this._savedRepliesStore.setSelectSavedReply(currentSelectedSavedReply)
          this.handleNewSavedReply(currentSelectedSavedReply)
        })

        return
      }

      this._savedRepliesStore.setSelectSavedReply(currentSelectedSavedReply)
      this.handleNewSavedReply(currentSelectedSavedReply)
    } else {
      if (this.hasChanges) {
        this._savedRepliesStore.handleLeaveWithoutSaving(() => {
          uiStore.changeRoute({
            path: SavedRepliesPath.root,
          })

          this._savedRepliesStore.setFirstSavedReply()
        })

        return
      }

      uiStore.changeRoute({
        path: SavedRepliesPath.root,
      })

      this._savedRepliesStore.setFirstSavedReply()
    }
  }

  onCreate = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      if (this.isEdit && this._savedReplay) {
        const { data } = await CannedMessagesApi.updateCannedMessagesById({
          ...this.createSavedReplyRequest,
          integration_id: this._savedReplay.integration_id,
          id: this._savedReplay.id,
        })
        runInAction(() => {
          this._savedRepliesStore.setSavedReplay(data)
        })
        toastStore.add({
          title: 'Reply changes saved',
          type: 'success',
        })
      } else {
        const { data } = await CannedMessagesApi.createCannedMessages(this.createSavedReplyRequest)
        toastStore.add({
          title: 'Saved reply created',
          type: 'success',
        })
        const savedReplay = new CannedMessage(data, this._entities)

        runInAction(() => {
          this._savedRepliesStore.setSelectSavedReply(savedReplay)
          this._savedRepliesStore.loadData({ selectedSavedReply: savedReplay })
        })
      }

      runInAction(() => {
        uiStore.changeRoute({
          path: SavedRepliesPath.root,
        })
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }
}
